'use client'

import { useState } from 'react'

import { MediaLightbox } from '@/components/media/MediaLightbox'
import { useProductPrimaryPhoto } from '@/components/orders/OrderCardPhoto'
import type { YMarketItem } from '@/lib/types'

interface Props {
  /**
   * The order line. Only its product members are read here.
   *
   * `pictureUrl` is deliberately not one of them: it is the provider's picture
   * for the provider's offer, and drawing it as the product's own photo is the
   * resemblance this surface exists to stop. A line the catalogue has not
   * attached therefore shows the empty mark, never a marketplace image.
   */
  item?: YMarketItem
  label: string
  className?: string
  interactive?: boolean
}

const LOADING_TITLE = 'Загружаем файл…'

/**
 * One order line's product tile, fetched from the product's own media.
 *
 * The byte route is Clerk-protected, so a bare `<img src>` answers 401 and
 * renders broken with nothing to report. The bytes are fetched with the bearer
 * token and handed to the DOM as a blob URL; the effect owns revoking it.
 */
export function ProductThumbnail({ item, label, className = '', interactive = true }: Props) {
  const reason = item?.productId
    ? item.productPrimaryMediaUnavailableReason ?? null
    : item?.productUnavailableReason ?? null
  const { objectUrl, loading } = useProductPrimaryPhoto(item)
  const [openObjectUrl, setOpenObjectUrl] = useState<string | null>(null)

  const image = objectUrl
    ? <img src={objectUrl} alt={label} className="size-full object-cover transition-transform duration-200 group-hover:scale-110" />
    : <span aria-hidden="true" className="size-2.5 rotate-45 rounded-[1px] bg-gold-deep/65" />

  const state = objectUrl ? 'available' : loading ? 'loading' : 'empty'
  const shell = `grid shrink-0 place-items-center overflow-hidden rounded-full border border-rule bg-bone text-gold-deep ${className}`

  if (objectUrl === null || !interactive) {
    return (
      <span
        data-testid="order-product-thumbnail"
        data-media-state={state}
        data-reason={state === 'empty' ? reason ?? undefined : undefined}
        title={loading ? LOADING_TITLE : undefined}
        className={shell}
      >
        {image}
      </span>
    )
  }

  return <>
    <button
      type="button"
      data-testid="order-product-thumbnail"
      data-media-state={state}
      title="Открыть фото товара"
      aria-label={`Открыть: ${label}`}
      onClick={() => setOpenObjectUrl(objectUrl)}
      className={`group cursor-zoom-in shadow-sm transition-shadow hover:border-gold/70 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${shell}`}
    >
      {image}
    </button>
    {openObjectUrl === objectUrl ? (
      // The blob URL, not the protected path: the stage renders it through
      // `src`, and the bytes were already fetched with the bearer token here.
      <MediaLightbox entries={[{ origin: 'catalog', url: objectUrl, alt: label }]} onClose={() => setOpenObjectUrl(null)} />
    ) : null}
  </>
}
